import { IconButton, TableRow } from "@mui/material";
import { GridMoreVertIcon } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import CommonTextField from "../../../../../common/components/functional/commonTextField/CommonTextField";
import { EllipsisTextField } from "../../../../../common/components/functional/ellipsisTextField/EllipsisTextField";
import { StyledTableCell } from "../../../../../common/components/functional/simpleTable/styled";
import formatCurrency from "../../../../../common/helpers/formatCurrency";
import {
  fromIsoDateString,
  leadingZerosDateFormat,
} from "../../../../../common/helpers/formatDateTime";
import { ActionMenu } from "../tableItems/ActionMenu";
import { formatFringeBenefitDetails } from "./mappers";
import { FringeBenefit, FringeBenefitType } from "./types";

type Props = {
  fringeBenefit: FringeBenefit;
  canEdit: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onCopy: (id: string) => void;
  fringeBenefitTypes: FringeBenefitType[];
};

export const FringeBenefitTableItem = ({
  fringeBenefit,
  canEdit,
  onEdit,
  onDelete,
  onCopy,
  fringeBenefitTypes,
}: Props) => {
  const { t } = useTranslation();
  const [actionMenuAnchor, setActionMenuAnchor] = useState<{
    target: null | HTMLElement;
    id: string;
  }>({ target: null, id: "" });

  const type = fringeBenefitTypes.find(
    (type) => type.code === fringeBenefit.expenseTypeCode,
  );
  const typeLabel = type?.name;
  const details = formatFringeBenefitDetails(
    fringeBenefit.details,
    type?.fields ?? [],
  );

  return (
    <TableRow>
      <StyledTableCell>
        <EllipsisTextField value={typeLabel} />
      </StyledTableCell>
      <StyledTableCell sx={{ minWidth: 90 }}>
        <CommonTextField
          fullWidth
          disabled
          value={fromIsoDateString(
            fringeBenefit.startDate,
            leadingZerosDateFormat,
          )}
        />
      </StyledTableCell>
      <StyledTableCell sx={{ minWidth: 90 }}>
        <CommonTextField
          fullWidth
          disabled
          value={fromIsoDateString(
            fringeBenefit.endDate,
            leadingZerosDateFormat,
          )}
        />
      </StyledTableCell>
      <StyledTableCell sx={{ display: "flex" }}>
        <EllipsisTextField value={fringeBenefit.description} />
      </StyledTableCell>
      <StyledTableCell>
        <EllipsisTextField value={details} />
      </StyledTableCell>
      <StyledTableCell>
        <EllipsisTextField value={fringeBenefit.additionalInformation} />
      </StyledTableCell>
      <StyledTableCell>
        <EllipsisTextField value={formatCurrency(fringeBenefit.amount)} />
      </StyledTableCell>
      <StyledTableCell>
        <EllipsisTextField
          value={t(
            fringeBenefit.includedInTotalSalary ? "common:yes" : "common:no",
          )}
        />
      </StyledTableCell>
      <StyledTableCell>
        {canEdit && (
          <IconButton
            onClick={(e) =>
              setActionMenuAnchor({
                target: e.currentTarget,
                id: fringeBenefit.id,
              })
            }
          >
            <GridMoreVertIcon />
          </IconButton>
        )}
      </StyledTableCell>
      <ActionMenu
        open={!!actionMenuAnchor.target}
        anchorEl={actionMenuAnchor}
        onClose={() => setActionMenuAnchor({ target: null, id: "" })}
        index={0}
        id={fringeBenefit.id}
        isRowEditable={true}
        onDeleteRow={() => onDelete(fringeBenefit.id)}
        onCopyRow={() => onCopy(fringeBenefit.id)}
        onEdit={() => onEdit(fringeBenefit.id)}
      />
    </TableRow>
  );
};
